<template>
  <div class="usuario-permissoes-tree">
    <v-checkbox
      v-if="!readonly"
      :input-value="allSelected"
      :indeterminate="someSelected && !allSelected"
      label="Marcar todos"
      dense
      hide-details
      color="primario"
      class="marcar-todos-checkbox"
      @change="toggleAll"
    />

    <v-treeview
      v-if="treeReady"
      :value="treeSelection"
      :items="treeItems"
      item-key="id"
      item-text="name"
      :selectable="!readonly"
      selection-type="leaf"
      open-on-click
      dense
      :open.sync="openNodes"
      color="primario"
      class="permissoes-treeview text-start"
      @input="onTreeSelection"
    >
    </v-treeview>

    <h4 class="text-center mt-4">Fornecedores</h4>
    <LineDivider></LineDivider>
    <FormCheckbox
      v-for="fornecedor in fornecedoresLocal"
      :key="fornecedor.idfornecedor"
      :label="fornecedor.nome"
      :value="fornecedor.fornecedor_granted"
      :val="true"
      :disabled="readonly"
      @change="val => toggleFornecedor(fornecedor, val)"
      class="mb-n5"
    />

    <h4 class="text-center mt-4">Permissões adicionais</h4>
    <LineDivider></LineDivider>
    <FormCheckbox
      label="Visualizar todos os clientes"
      :value="flagsLocal.permissao_atendente"
      :val="true"
      :disabled="readonly"
      @change="val => updateFlag('permissao_atendente', val)"
      class="mb-n5"
    />
    <FormCheckbox
      label="Alterar senha do cliente"
      :value="flagsLocal.permissao_alterarsenha"
      :val="true"
      :disabled="readonly"
      @change="val => updateFlag('permissao_alterarsenha', val)"
    />
  </div>
</template>

<script>
import * as _ from "lodash";
import FormCheckbox from "@/components/form/FormCheckbox";
import LineDivider from "@/components/LineDivider";

export default {
  name: "UsuarioPermissoesTree",
  components: {
    FormCheckbox,
    LineDivider
  },
  props: {
    itensMenu: {
      type: Array,
      default: () => []
    },
    fornecedores: {
      type: Array,
      default: () => []
    },
    flags: {
      type: Object,
      default: () => ({
        permissao_atendente: false,
        permissao_alterarsenha: false
      })
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    itensMenuLocal: [],
    fornecedoresLocal: [],
    flagsLocal: {
      permissao_atendente: false,
      permissao_alterarsenha: false
    },
    treeItems: [],
    treeSelection: [],
    openNodes: [],
    treeReady: false,
    isSyncingTree: false,
    skipNextPropSync: false
  }),
  computed: {
    allSelected() {
      if (!this.itensMenuLocal.length) {
        return false;
      }
      return this.itensMenuLocal.every(item => item.tem_permissao);
    },
    someSelected() {
      return this.itensMenuLocal.some(item => item.tem_permissao);
    }
  },
  watch: {
    itensMenu: {
      immediate: true,
      handler(value) {
        if (this.skipNextPropSync) {
          return;
        }
        this.applyItensMenuFromProp(value || []);
      }
    },
    fornecedores: {
      immediate: true,
      handler(value) {
        if (this.skipNextPropSync) {
          return;
        }
        this.fornecedoresLocal = (value || []).map(item => ({ ...item }));
      }
    },
    flags: {
      immediate: true,
      handler(value) {
        if (this.skipNextPropSync) {
          return;
        }
        this.flagsLocal = {
          permissao_atendente: Boolean(value && value.permissao_atendente),
          permissao_alterarsenha: Boolean(value && value.permissao_alterarsenha)
        };
      }
    }
  },
  methods: {
    applyItensMenuFromProp(itensMenu) {
      this.isSyncingTree = true;
      this.itensMenuLocal = itensMenu.map(item => ({ ...item }));
      this.buildTreeItems();
      this.syncTreeSelectionFromItems();
      this.$nextTick(() => {
        this.isSyncingTree = false;
      });
    },
    buildTreeItems() {
      const grouped = _.groupBy(this.itensMenuLocal, item =>
        item.grupo ? String(item.grupo) : "Outros"
      );

      this.treeItems = Object.keys(grouped)
        .sort((a, b) => a.localeCompare(b, "pt-BR"))
        .map(grupo => ({
          id: `grupo-${grupo}`,
          name: grupo,
          children: grouped[grupo]
            .slice()
            .sort((a, b) => {
              const orderA = a.list_order != null ? a.list_order : 0;
              const orderB = b.list_order != null ? b.list_order : 0;
              if (orderA !== orderB) return orderA - orderB;
              return String(a.nome || "").localeCompare(
                String(b.nome || ""),
                "pt-BR"
              );
            })
            .map(item => ({
              id: `item-${item.iditem}`,
              name: item.nome,
              iditem: item.iditem
            }))
        }));

      this.treeReady = this.treeItems.length > 0;
    },
    emitChange() {
      this.skipNextPropSync = true;
      const payload = {
        itensMenu: this.itensMenuLocal,
        fornecedores: this.fornecedoresLocal,
        flags: { ...this.flagsLocal }
      };
      this.$emit("input", payload);
      this.$emit("change", payload);
      this.$nextTick(() => {
        this.skipNextPropSync = false;
      });
    },
    syncTreeSelectionFromItems() {
      const nextSelection = this.itensMenuLocal
        .filter(item => item.tem_permissao)
        .map(item => `item-${item.iditem}`);

      if (
        _.isEqual(
          nextSelection.slice().sort(),
          this.treeSelection.slice().sort()
        )
      ) {
        return;
      }

      this.treeSelection = nextSelection;
    },
    onTreeSelection(selected) {
      if (this.isSyncingTree || this.readonly) {
        return;
      }

      const selectedSet = new Set(selected || []);
      let hasChanges = false;

      const nextItensMenu = this.itensMenuLocal.map(item => {
        const temPermissao = selectedSet.has(`item-${item.iditem}`);
        if (item.tem_permissao !== temPermissao) {
          hasChanges = true;
        }
        return temPermissao === item.tem_permissao
          ? item
          : { ...item, tem_permissao: temPermissao };
      });

      if (!hasChanges) {
        return;
      }

      this.itensMenuLocal = nextItensMenu;
      this.treeSelection = selected || [];
      this.emitChange();
    },
    toggleAll(checked) {
      this.isSyncingTree = true;
      this.itensMenuLocal = this.itensMenuLocal.map(item => ({
        ...item,
        tem_permissao: Boolean(checked)
      }));
      this.syncTreeSelectionFromItems();
      this.emitChange();
      this.$nextTick(() => {
        this.isSyncingTree = false;
      });
    },
    toggleFornecedor(fornecedor, checked) {
      this.fornecedoresLocal = this.fornecedoresLocal.map(current =>
        current.idfornecedor === fornecedor.idfornecedor
          ? { ...current, fornecedor_granted: Boolean(checked) }
          : current
      );
      this.emitChange();
    },
    updateFlag(flag, checked) {
      this.flagsLocal = {
        ...this.flagsLocal,
        [flag]: Boolean(checked)
      };
      this.emitChange();
    }
  }
};
</script>

<style scoped>
.usuario-permissoes-tree {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.87);
}

.marcar-todos-checkbox {
  margin-top: 0;
  padding-top: 0;
}

.marcar-todos-checkbox ::v-deep .v-label {
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
}

.permissoes-treeview {
  margin-top: 8px;
}

.permissoes-treeview ::v-deep .v-treeview-node__root {
  min-height: 32px;
}

.permissoes-treeview ::v-deep .v-treeview-node__label {
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
}

.permissoes-treeview ::v-deep .v-treeview-node__checkbox {
  margin-right: 8px;
  margin-left: 10px;
}
</style>
